/**
 * Grammar quiz state store.
 *
 * Zustand store for managing grammar quiz state including answers, results,
 * checked questions, and answer visibility.
 *
 * @module useGrammarQuizStore
 */

import { create } from 'zustand';
import { withDevTools } from './storeUtils';
import {
  type GrammarQuiz,
  type QuizQuestion,
  type QuizSection,
  type QuizBlank,
} from '@/lib/contentLoader';

/**
 * Grammar quiz store state interface
 */
interface GrammarQuizState {
  /** User answers for each question */
  quizAnswers: Record<string, string[]>;
  /** Results (correct/incorrect) for each question */
  quizResults: Record<string, boolean[]>;
  /** Whether to show results after submission */
  showResults: boolean;
  /** Checked questions with their results */
  checkedQuestions: Record<string, boolean[]>;
  /** Questions with shown answers */
  shownAnswers: Record<string, boolean>;

  // Actions
  /** Set answer for a question and blank */
  setAnswer: (questionId: string, blankIndex: number, value: string, totalBlanks: number) => void;
  /** Check answers for a specific question */
  checkQuestion: (questionId: string, quiz: GrammarQuiz | null) => void;
  /** Toggle show answer for a question */
  showAnswer: (questionId: string) => void;
  /** Submit quiz and calculate all results */
  submitQuiz: (quiz: GrammarQuiz | null) => void;
  /** Reset quiz to initial state */
  resetQuiz: () => void;
  /** Calculate statistics (total and expected answers) */
  calculateStats: (quiz: GrammarQuiz | null) => { total: number; correct: number };
  /** Check if all questions are answered */
  getAllAnswered: (quiz: GrammarQuiz | null) => boolean;
  /** Check answers for a question (utility function) */
  checkQuestionAnswers: (questionId: string, quiz: GrammarQuiz | null) => boolean[];
}

/**
 * Check if a blank answer is correct
 */
function checkBlankAnswer(blank: QuizBlank, userAnswer: string): boolean {
  if (!userAnswer) return false;

  const normalizedAnswer = blank.normalize ? userAnswer.toLowerCase() : userAnswer;
  return blank.acceptedAnswers.some((correct: string) => {
    const normalizedCorrect = blank.normalize ? correct.toLowerCase() : correct;
    if (blank.caseSensitive) {
      return normalizedAnswer === normalizedCorrect;
    }
    return normalizedAnswer.toLowerCase() === normalizedCorrect.toLowerCase();
  });
}

/**
 * Grammar quiz Zustand store.
 *
 * Manages quiz state including:
 * - User answers for fill-in-the-blank questions
 * - Answer checking and results
 * - Answer visibility toggles
 * - Quiz submission and statistics
 *
 * @example
 * ```typescript
 * const { quizAnswers, setAnswer, checkQuestion, submitQuiz } = useGrammarQuizStore();
 * ```
 */
export const useGrammarQuizStore = create<GrammarQuizState>()(
  withDevTools(
    (set, get) => ({
      // Initial state
      quizAnswers: {},
      quizResults: {},
      showResults: false,
      checkedQuestions: {},
      shownAnswers: {},

      // Actions
      setAnswer: (questionId: string, blankIndex: number, value: string, totalBlanks: number) => {
        set((state) => {
          const currentAnswers = state.quizAnswers[questionId] || [];
          const newAnswers = [...currentAnswers];
          newAnswers[blankIndex] = value;
          // Ensure array is long enough
          while (newAnswers.length < totalBlanks) {
            newAnswers.push('');
          }
          return {
            quizAnswers: {
              ...state.quizAnswers,
              [questionId]: newAnswers,
            },
          };
        });
      },

      checkQuestionAnswers: (questionId: string, quiz: GrammarQuiz | null): boolean[] => {
        if (!quiz) return [];

        const state = get();
        const question =
          quiz.questions?.find((q: QuizQuestion) => q.id === questionId) ||
          quiz.sections?.flatMap((s: QuizSection) => s.questions).find((q: QuizQuestion) => q.id === questionId);

        if (!question?.blanks) return [];

        const questionAnswers = state.quizAnswers[questionId] ?? [];

        // Check all blanks for this question
        return question.blanks.map((blank: QuizBlank, idx: number) => {
          const answer = (questionAnswers[idx] || '').trim();
          return checkBlankAnswer(blank, answer);
        });
      },

      checkQuestion: (questionId: string, quiz: GrammarQuiz | null) => {
        const results = get().checkQuestionAnswers(questionId, quiz);
        set((state) => ({
          checkedQuestions: {
            ...state.checkedQuestions,
            [questionId]: results,
          },
          quizResults: {
            ...state.quizResults,
            [questionId]: results,
          },
        }));
      },

      showAnswer: (questionId: string) => {
        set((state) => ({
          shownAnswers: {
            ...state.shownAnswers,
            [questionId]: !state.shownAnswers[questionId],
          },
        }));
      },

      submitQuiz: (quiz: GrammarQuiz | null) => {
        if (!quiz) return;

        const state = get();
        const allResults: Record<string, boolean[]> = {};

        const processQuestion = (q: QuizQuestion) => {
          if (q.blanks && q.blanks.length > 0) {
            allResults[q.id] = state.checkQuestionAnswers(q.id, quiz);
          }
        };

        if (quiz.questions) {
          quiz.questions.forEach(processQuestion);
        }

        if (quiz.sections) {
          quiz.sections.forEach((section: QuizSection) => {
            section.questions.forEach(processQuestion);
          });
        }

        set({
          quizResults: allResults,
          showResults: true,
        });
      },

      resetQuiz: () => {
        set({
          showResults: false,
          quizAnswers: {},
          quizResults: {},
          checkedQuestions: {},
          shownAnswers: {},
        });
      },

      calculateStats: (quiz: GrammarQuiz | null): { total: number; correct: number } => {
        if (!quiz) return { total: 0, correct: 0 };

        const state = get();
        let total = 0;
        let correct = 0;

        const processQuestion = (q: QuizQuestion) => {
          if (!q.blanks || q.blanks.length === 0) return;
          total += q.blanks.length;
          const results = state.quizResults[q.id];
          if (Array.isArray(results)) {
            correct += results.filter(Boolean).length;
          }
        };

        if (quiz.questions) {
          quiz.questions.forEach(processQuestion);
        }
        if (quiz.sections) {
          quiz.sections.forEach((section: QuizSection) => {
            section.questions.forEach(processQuestion);
          });
        }

        return { total, correct };
      },

      getAllAnswered: (quiz: GrammarQuiz | null): boolean => {
        if (!quiz) return false;

        const state = get();

        const isQuestionAnswered = (q: QuizQuestion): boolean => {
          if (!q.blanks || q.blanks.length === 0) return true;
          const answers = state.quizAnswers[q.id];
          if (!answers || !Array.isArray(answers)) return false;
          return q.blanks.every((_: QuizBlank, idx: number) => answers[idx]?.trim());
        };

        const questionsAnswered = quiz.questions?.every(isQuestionAnswered) || false;
        const sectionsAnswered =
          quiz.sections?.every((s: QuizSection) => s.questions.every(isQuestionAnswered)) || false;

        return questionsAnswered && sectionsAnswered;
      },
    }),
    'GrammarQuizStore',
  ),
);

